import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, Stethoscope, MessageSquare, Languages, ListChecks,
  AlertTriangle, MapPin, Globe2, ShieldCheck, Layers, ArrowRight,
  Star, Quote, Tablet, BookMarked,
} from "lucide-react";

// Book launch section — premium Italian-only block to promote
// "Guida teorico-pratica per superare l'esame di riconoscimento" 2nd ed. 2026.
// Designed dark + Italian flag accents (verde/bianco/rosso). Drops on top of
// the existing Landing layout, before the FooterSection.

const BOOK_COVER =
  "https://customer-assets.emergentagent.com/job_italian-med-test/artifacts/97ypru14_ChatGPT%20Image%2024%20may%202026%2C%2009_10_29%20p.m..png";

// 4 Amazon purchase variants. Hardcover is split into Tomo I + Tomo II
// (sold separately on Amazon). The canonical URL used in JSON-LD schema
// points to Tomo I as the entry point of the series.
const AMAZON_URL = "https://amzn.eu/d/0i52yi2U"; // hardcover tomo I (canonical)
const FORMATS = [
  { id: "hardcover_t1", label: "Copertina rigida · Tomo I",  hint: "Edizione premium · Vol. 1", url: "https://amzn.eu/d/0i52yi2U", badge: "Top" },
  { id: "hardcover_t2", label: "Copertina rigida · Tomo II", hint: "Edizione premium · Vol. 2", url: "https://amzn.eu/d/06TEb1Ih", badge: "Top" },
  { id: "paperback",    label: "Copertina morbida",          hint: "Più economica",             url: "https://amzn.eu/d/08yLzCZx", badge: null  },
  { id: "kindle",       label: "Kindle / eBook",             hint: "Lettura digitale",          url: "https://amzn.eu/d/0irboDrK", badge: null  },
];

// Italian flag colors tuned for a dark theme.
const C = {
  green:  "#3CAB6F",
  greenSoft: "rgba(60,171,111,0.18)",
  red:    "#C9314A",
  redSoft:"rgba(201,49,74,0.18)",
  cream:  "#F4EFE6",
  ink:    "#0B0E14",
  panel:  "#10141B",
  panelEdge: "rgba(244,239,230,0.08)",
};

const FADE_UP = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

const VIEWPORT = { once: true, margin: "-80px" };

const BENEFITS = [
  { icon: Stethoscope,    title: "Preparazione all'esame orale",
    body: "Tecnica, gestione del tempo, ponti narrativi quando la commissione interrompe." },
  { icon: BookOpen,       title: "Casi clinici reali",
    body: "Ricostruiti da sessioni reali del riconoscimento ministeriale italiano." },
  { icon: ListChecks,     title: "Strategie pratiche",
    body: "Schemi mentali per rispondere senza perdere il filo davanti ai 3 commissari." },
  { icon: Languages,      title: "Terminologia medica italiana",
    body: "Glossario clinico essenziale: ciò che dirai DAVVERO il giorno dell'esame." },
  { icon: MessageSquare,  title: "Simulazioni dell'orale",
    body: "Domande tipo commissione, con risposte modello brevi e ad alto impatto." },
  { icon: AlertTriangle,  title: "Errori frequenti da evitare",
    body: "Le 15 trappole che fanno bocciare i candidati meglio preparati." },
  { icon: MapPin,         title: "Esperienza reale del SSN",
    body: "Come si lavora davvero in Italia: contratti, ruoli, percorsi di carriera." },
];

const AUDIENCE = [
  { icon: Globe2,         title: "Medici stranieri",
    body: "Chiunque abbia conseguito la laurea fuori dall'Italia." },
  { icon: ShieldCheck,    title: "Medici latinoamericani",
    body: "Argentina, Cuba, Venezuela, Colombia, Perù, Brasile, Messico…" },
  { icon: Layers,         title: "Medici in riconoscimento",
    body: "Pratica al Ministero della Salute in corso o pianificata." },
  { icon: MapPin,         title: "Medici che vogliono trasferirsi",
    body: "Chi vuole iniziare oggi un percorso reale verso l'Italia." },
];

const TESTIMONIALS = [
  { name: "Dr.ssa Camila R.", role: "Argentina · superato 2025",
    body: "La guida più completa che abbia trovato. Mi sono presentata all'orale con uno schema chiaro in testa." },
  { name: "Dr. Andrés M.", role: "Colombia · superato 2025",
    body: "Mi ha aiutato tantissimo per l'orale. Le simulazioni sembrano scritte da chi era seduto in commissione." },
  { name: "Dr.ssa Valentina P.", role: "Venezuela · in preparazione 2026",
    body: "Finalmente ho capito come prepararmi davvero. Niente teoria astratta: solo cose che servono il giorno X." },
];

const STATS = [
  { kpi: "600+",      label: "pagine di esperienza reale" },
  { kpi: "2",         label: "volumi · copertina rigida" },
  { kpi: "2026",      label: "seconda edizione aggiornata" },
  { kpi: "100%",      label: "orientato ai medici stranieri" },
];

// JSON-LD: Book + EducationalOrganization for richer Google rich results.
function SchemaMarkup() {
  useEffect(() => {
    const id = "book-launch-schema";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    s.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Book",
        "name": "Guida teorico-pratica per superare l'esame di riconoscimento della laurea in medicina in Italia",
        "alternateName": "Seconda Edizione 2026",
        "author": { "@type": "Person", "name": "Lucas Pablo Terzano" },
        "bookEdition": "2ª · Aggiornata 2026",
        "bookFormat": "https://schema.org/Hardcover",
        "inLanguage": "it",
        "image": BOOK_COVER,
        "numberOfPages": 600,
        "url": AMAZON_URL,
        "offers": {
          "@type": "Offer",
          "url": AMAZON_URL,
          "availability": "https://schema.org/InStock",
          "priceCurrency": "EUR",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "name": "Medicos para Italia · MediQuiz IT",
        "url": "https://esamericonoscimento.it",
        "logo": "https://customer-assets.emergentagent.com/job_italian-med-test/artifacts/5mezey23_Logo%20MPI.jpeg",
        "description":
          "Libro e accademia specializzata per medici stranieri che desiderano lavorare legalmente in Italia e superare l'esame di riconoscimento.",
        "sameAs": [AMAZON_URL],
      },
    ]);
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, []);
  return null;
}

// Italian flag accent stripe (subtle, on dark bg).
function FlagStripe() {
  return (
    <div className="flex h-1 w-24 overflow-hidden rounded-full"
         aria-hidden="true">
      <span className="flex-1" style={{ background: C.green }} />
      <span className="flex-1" style={{ background: C.cream }} />
      <span className="flex-1" style={{ background: C.red }} />
    </div>
  );
}

// Icon mapping for each format.
const FORMAT_ICONS = {
  hardcover_t1: BookOpen,
  hardcover_t2: BookOpen,
  paperback:    BookMarked,
  kindle:       Tablet,
};

// 4-format picker. Variant 'dark' is used inside dark/black bg sections,
// 'gold' is used on the final CTA where we want maximum pop.
function FormatPicker({ variant = "dark", testIdPrefix = "book-format" }) {
  return (
    <div className="w-full" data-testid={`${testIdPrefix}-picker`}>
      <div className="text-[11px] tracking-[0.28em] font-semibold mb-3"
           style={{ color: variant === "gold" ? C.green : C.green }}>
        SCEGLI IL TUO FORMATO
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {FORMATS.map((f) => {
          const Icon = FORMAT_ICONS[f.id] || BookOpen;
          const isHardcover = f.id.startsWith("hardcover");
          return (
            <a
              key={f.id}
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testIdPrefix}-${f.id}`}
              className="group relative flex items-start gap-3 rounded-xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-12px_rgba(201,49,74,0.45)]"
              style={{
                background: isHardcover
                  ? `linear-gradient(135deg, ${C.red} 0%, #E04B66 60%, ${C.red} 100%)`
                  : "rgba(255,255,255,0.04)",
                color: isHardcover ? "#FFF" : C.cream,
                border: isHardcover
                  ? "1px solid rgba(255,255,255,0.18)"
                  : `1px solid ${C.panelEdge}`,
                boxShadow: isHardcover
                  ? "0 12px 30px -10px rgba(201,49,74,0.5)"
                  : "none",
              }}
            >
              {f.badge && (
                <span
                  className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest"
                  style={{ background: C.green, color: "#0B0E14" }}
                >
                  {f.badge}
                </span>
              )}
              <span
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg shrink-0"
                style={{
                  background: isHardcover ? "rgba(255,255,255,0.16)" : C.greenSoft,
                  border: isHardcover
                    ? "1px solid rgba(255,255,255,0.22)"
                    : `1px solid ${C.green}40`,
                }}
              >
                <Icon className="h-4 w-4"
                      style={{ color: isHardcover ? "#FFF" : C.green }} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm leading-tight">
                  {f.label}
                </span>
                <span className="block text-[11px] mt-0.5 leading-tight"
                      style={{ color: isHardcover ? "rgba(255,255,255,0.85)" : "rgba(244,239,230,0.55)" }}>
                  {f.hint}
                </span>
              </span>
              <ArrowRight className="h-4 w-4 mt-1 shrink-0 transition-transform group-hover:translate-x-0.5"
                          style={{ color: isHardcover ? "#FFF" : C.green }} />
            </a>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-center sm:text-left"
         style={{ color: "rgba(244,239,230,0.5)" }}>
        Tutti gli acquisti tramite Amazon · spedizione e gestione gestite da Amazon.
      </p>
    </div>
  );
}

function SecondaryCTA({ href, children, testId, onClick }) {
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testId}
      className="group inline-flex items-center justify-center gap-2 rounded-xl px-7 py-3.5 font-semibold text-base transition-all hover:scale-[1.02]"
      style={{
        background: "transparent",
        color: C.cream,
        border: `1px solid ${C.greenSoft}`,
      }}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  style={{ color: C.green }} />
    </a>
  );
}

// ─── HERO ────────────────────────────────────────────────────────────────────
function BookHero() {
  return (
    <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] items-center">
      <motion.div initial={FADE_UP.hidden} whileInView={FADE_UP.show} viewport={VIEWPORT}>
        <div className="flex items-center gap-3 mb-5">
          <FlagStripe />
          <span className="text-[11px] tracking-[0.32em] font-semibold"
                style={{ color: C.green }}>
            NUOVO · SECONDA EDIZIONE 2026
          </span>
        </div>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] font-medium"
            style={{ color: C.cream }}>
          Ora disponibile la <em className="italic" style={{ color: C.green }}>nuova</em>{" "}
          <em className="italic" style={{ color: C.red }}>seconda edizione</em> 2026
        </h2>
        <p className="mt-5 text-lg leading-relaxed"
           style={{ color: "rgba(244,239,230,0.78)" }}>
          La guida definitiva per medici stranieri che vogliono lavorare in Italia.
          <br className="hidden sm:block" />
          Oltre 600 pagine, 2 volumi · scritta da chi è già passato in commissione.
        </p>
        <div className="mt-9">
          <FormatPicker testIdPrefix="book-hero-format" />
        </div>
        <div className="mt-4">
          <SecondaryCTA href="/vip" testId="book-hero-academy-cta">
            Scopri l&apos;Accademia
          </SecondaryCTA>
        </div>
        <div className="mt-7 flex items-center gap-4 text-xs"
             style={{ color: "rgba(244,239,230,0.55)" }}>
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: C.green }} />
            Spedizione Amazon
          </span>
          <span className="inline-flex items-center gap-1">
            <BookOpen className="h-3.5 w-3.5" style={{ color: C.green }} />
            Copertina rigida
          </span>
          <span className="inline-flex items-center gap-1">
            <Layers className="h-3.5 w-3.5" style={{ color: C.green }} />
            2 volumi
          </span>
        </div>
      </motion.div>

      {/* 3D book mockup */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, rotateY: -25 }}
        whileInView={{ opacity: 1, scale: 1, rotateY: -8 }}
        viewport={VIEWPORT}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative mx-auto"
        style={{ perspective: "1400px" }}
      >
        <div className="relative" style={{ transformStyle: "preserve-3d" }}>
          {/* Floor reflection */}
          <div
            className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[80%] h-12 blur-2xl"
            style={{ background: `radial-gradient(ellipse, ${C.redSoft}, transparent 70%)` }}
            aria-hidden="true"
          />
          {/* Spine edge */}
          <div
            className="absolute top-0 left-[-14px] h-full w-[14px] rounded-l-md"
            style={{
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 100%)",
              transform: "skewY(-6deg) translateY(8px)",
            }}
            aria-hidden="true"
          />
          {/* Cover */}
          <img
            src={BOOK_COVER}
            alt="Copertina del libro · Guida teorico-pratica per l'esame di riconoscimento della laurea in medicina in Italia · Seconda edizione 2026"
            loading="lazy"
            className="relative w-[78%] mx-auto block rounded-md shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]"
            style={{
              transform: "rotateY(-6deg) rotateX(2deg)",
              border: `1px solid ${C.panelEdge}`,
            }}
            data-testid="book-cover-image"
          />
          {/* Gold edge gleam */}
          <div
            className="absolute top-0 right-[11%] h-full w-[3px] rounded-sm pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, transparent 0%, rgba(255,235,160,0.5) 50%, transparent 100%)",
              transform: "rotateY(-6deg) translateX(-1px)",
            }}
            aria-hidden="true"
          />
        </div>
      </motion.div>
    </div>
  );
}

// ─── BENEFITS ────────────────────────────────────────────────────────────────
function BenefitsGrid() {
  return (
    <div className="mt-24">
      <motion.div initial={FADE_UP.hidden} whileInView={FADE_UP.show} viewport={VIEWPORT}
                  className="text-center mb-12">
        <div className="text-[11px] tracking-[0.32em] font-semibold mb-3"
             style={{ color: C.green }}>// COSA TROVERAI NEL LIBRO</div>
        <h3 className="font-serif text-3xl sm:text-4xl"
            style={{ color: C.cream }}>
          Preparazione <em className="italic" style={{ color: C.green }}>completa</em>,
          niente lasciato al caso
        </h3>
      </motion.div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {BENEFITS.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.div
              key={b.title}
              initial={FADE_UP.hidden}
              whileInView={FADE_UP.show}
              viewport={VIEWPORT}
              transition={{ duration: 0.6, delay: 0.04 * i, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`book-benefit-${i + 1}`}
              className="group relative rounded-2xl p-6 transition-all hover:-translate-y-0.5"
              style={{
                background: C.panel,
                border: `1px solid ${C.panelEdge}`,
              }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl mb-4 transition-colors group-hover:scale-105"
                   style={{ background: C.greenSoft, border: `1px solid ${C.green}40` }}>
                <Icon className="h-5 w-5" style={{ color: C.green }} />
              </div>
              <div className="font-serif text-base mb-2"
                   style={{ color: C.cream }}>{b.title}</div>
              <div className="text-sm leading-relaxed"
                   style={{ color: "rgba(244,239,230,0.65)" }}>{b.body}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ─── AUDIENCE ────────────────────────────────────────────────────────────────
function AudienceGrid() {
  return (
    <div className="mt-24">
      <motion.div initial={FADE_UP.hidden} whileInView={FADE_UP.show} viewport={VIEWPORT}
                  className="text-center mb-12">
        <div className="text-[11px] tracking-[0.32em] font-semibold mb-3"
             style={{ color: C.red }}>// A CHI È RIVOLTO</div>
        <h3 className="font-serif text-3xl sm:text-4xl"
            style={{ color: C.cream }}>
          Un libro pensato per <em className="italic" style={{ color: C.red }}>chi vuole davvero
          fare il salto</em>
        </h3>
      </motion.div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {AUDIENCE.map((a, i) => {
          const Icon = a.icon;
          return (
            <motion.div
              key={a.title}
              initial={FADE_UP.hidden}
              whileInView={FADE_UP.show}
              viewport={VIEWPORT}
              transition={{ duration: 0.6, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`book-audience-${i + 1}`}
              className="rounded-2xl p-6 transition-all hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(160deg, ${C.panel} 0%, #0E1117 100%)`,
                border: `1px solid ${C.panelEdge}`,
              }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl mb-4"
                   style={{ background: C.redSoft, border: `1px solid ${C.red}40` }}>
                <Icon className="h-5 w-5" style={{ color: C.red }} />
              </div>
              <div className="font-serif text-base mb-2"
                   style={{ color: C.cream }}>{a.title}</div>
              <div className="text-sm leading-relaxed"
                   style={{ color: "rgba(244,239,230,0.65)" }}>{a.body}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ─── IMPACT BANNER ───────────────────────────────────────────────────────────
function ImpactBanner() {
  return (
    <motion.div
      initial={FADE_UP.hidden}
      whileInView={FADE_UP.show}
      viewport={VIEWPORT}
      data-testid="book-impact-banner"
      className="mt-24 relative overflow-hidden rounded-3xl p-10 sm:p-12"
      style={{
        background:
          "linear-gradient(135deg, #0E1117 0%, #161B25 50%, #0E1117 100%)",
        border: `1px solid ${C.panelEdge}`,
      }}
    >
      {/* Decorative vertical flag bars */}
      <div className="absolute right-0 top-0 h-full w-1.5"
           style={{ background: C.green }} aria-hidden="true" />
      <div className="absolute right-1.5 top-0 h-full w-1.5"
           style={{ background: C.cream }} aria-hidden="true" />
      <div className="absolute right-3 top-0 h-full w-1.5"
           style={{ background: C.red }} aria-hidden="true" />

      <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr] items-center">
        <div>
          <FlagStripe />
          <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight"
              style={{ color: C.cream }}>
            Oltre <em className="italic" style={{ color: C.green }}>600 pagine</em> di esperienza reale e{" "}
            <em className="italic" style={{ color: C.red }}>preparazione medica</em>
          </h3>
          <p className="mt-4 text-base leading-relaxed"
             style={{ color: "rgba(244,239,230,0.7)" }}>
            Scritto da un medico che è entrato in Italia con il riconoscimento ministeriale.
            Niente fluff, niente teoria sterile: ciò che ti serve davvero per superare l&apos;esame.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl p-5"
                 style={{ background: "rgba(255,255,255,0.03)",
                          border: `1px solid ${C.panelEdge}` }}>
              <div className="font-serif text-3xl" style={{ color: C.green }}>
                {s.kpi}
              </div>
              <div className="mt-1 text-xs leading-snug"
                   style={{ color: "rgba(244,239,230,0.65)" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ─── TESTIMONIALS ────────────────────────────────────────────────────────────
function TestimonialsBlock() {
  return (
    <div className="mt-24">
      <motion.div initial={FADE_UP.hidden} whileInView={FADE_UP.show} viewport={VIEWPORT}
                  className="text-center mb-10">
        <div className="text-[11px] tracking-[0.32em] font-semibold mb-3"
             style={{ color: C.green }}>// TESTIMONIANZE</div>
        <h3 className="font-serif text-3xl sm:text-4xl"
            style={{ color: C.cream }}>
          Colleghi che <em className="italic" style={{ color: C.green }}>ce l&apos;hanno fatta</em>
        </h3>
      </motion.div>
      <div className="grid gap-4 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.name}
            initial={FADE_UP.hidden}
            whileInView={FADE_UP.show}
            viewport={VIEWPORT}
            transition={{ duration: 0.6, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`book-testimonial-${i + 1}`}
            className="relative rounded-2xl p-6 flex flex-col"
            style={{
              background: C.panel,
              border: `1px solid ${C.panelEdge}`,
            }}
          >
            <Quote className="h-5 w-5 mb-3" style={{ color: C.red }} />
            <p className="text-sm leading-relaxed flex-1"
               style={{ color: "rgba(244,239,230,0.85)" }}>{t.body}</p>
            <div className="mt-5 pt-4 flex items-center justify-between"
                 style={{ borderTop: `1px solid ${C.panelEdge}` }}>
              <div>
                <div className="text-sm font-medium" style={{ color: C.cream }}>{t.name}</div>
                <div className="text-[11px] mt-0.5"
                     style={{ color: "rgba(244,239,230,0.55)" }}>{t.role}</div>
              </div>
              <div className="flex gap-0.5">
                {[0,1,2,3,4].map(s => (
                  <Star key={s} className="h-3.5 w-3.5 fill-current"
                        style={{ color: C.green }} />
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─── FINAL CTA ───────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <motion.div
      initial={FADE_UP.hidden}
      whileInView={FADE_UP.show}
      viewport={VIEWPORT}
      data-testid="book-final-cta"
      className="mt-24 relative overflow-hidden rounded-3xl p-10 sm:p-16 text-center"
      style={{
        background:
          "radial-gradient(ellipse at 20% 0%, rgba(60,171,111,0.18) 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(201,49,74,0.20) 0%, transparent 50%), #0B0E14",
        border: `1px solid ${C.panelEdge}`,
      }}
    >
      <FlagStripe />
      <h3 className="mt-5 font-serif text-3xl sm:text-5xl leading-tight"
          style={{ color: C.cream }}>
        Inizia oggi il tuo{" "}
        <em className="italic" style={{ color: C.green }}>percorso</em> come{" "}
        <em className="italic" style={{ color: C.red }}>medico in Italia</em>
      </h3>
      <p className="mt-5 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed"
         style={{ color: "rgba(244,239,230,0.78)" }}>
        Preparazione reale per medici che vogliono fare il salto professionale.
        Il libro è solo l&apos;inizio: l&apos;Accademia online ti accompagna fino al giorno dell&apos;esame.
      </p>
      <div className="mt-9 max-w-3xl mx-auto">
        <FormatPicker variant="gold" testIdPrefix="book-final-format" />
      </div>
      <div className="mt-5 flex justify-center">
        <SecondaryCTA href="/vip" testId="book-final-academy-cta">
          Scopri l&apos;Accademia
        </SecondaryCTA>
      </div>
    </motion.div>
  );
}

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────────
export default function BookLaunchSection() {
  return (
    <section
      id="libro-2026"
      aria-label="Libro 2026 · Guida per medici stranieri in Italia"
      data-testid="book-launch-section"
      className="relative py-24 sm:py-32"
      style={{ background: C.ink }}
    >
      <SchemaMarkup />
      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(60,171,111,0.3) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(201,49,74,0.3) 0%, transparent 40%)",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-6xl mx-auto px-6 sm:px-8">
        <BookHero />
        <BenefitsGrid />
        <AudienceGrid />
        <ImpactBanner />
        <TestimonialsBlock />
        <FinalCTA />
      </div>
    </section>
  );
}
